import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import toast from "react-hot-toast";
import useObjects from "@/apis/objects";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import useFilter, { QueryData } from "@/hooks/useFilter";
import useAsignProtect from "@/apis/protect";
import { apis } from "@/apis";
import { useUserContext } from "./user";
import { useAuth } from "@/hooks/useAuth";
import { getId } from "@/lib/helper";
import { useMediaQuery } from "@mui/material";

// Define the context type
interface ObjectContextType {
  currentObject: any;
  isLoading: boolean;
  updateOrAddItem: (data: any) => void;
  setFilterOption: React.Dispatch<React.SetStateAction<any>>;
  setQuery: React.Dispatch<React.SetStateAction<any>>;
  removeQueryKey: (key: string) => void;
  filterOption: QueryData;
  objectsList: any;
  setPaginatorData: React.Dispatch<React.SetStateAction<any>>;
  refetchPaginationData: (next: number) => void;
  eligibility: boolean;
  missingFields: string[];
  falseBooleanFields: string[];
  category: any;
  filledFields: any;
  setFilledFields: React.Dispatch<React.SetStateAction<any>>;
  addOrDeleteObjectId: (id: string) => void;
  objectIds: string[];
  setObjectIds: React.Dispatch<React.SetStateAction<string[]>>;
  removeAllQuery: () => void;
  removeOptionKey: (key: string) => void;
  locations: any;
  setLocations: React.Dispatch<React.SetStateAction<any>>;
  isapPageVisited: boolean;
  setIsapPageVisited: React.Dispatch<React.SetStateAction<boolean>>;
  valuationPricing: any;
  setValuationPricing: React.Dispatch<React.SetStateAction<any>>;
  filterList: any;
  isCropModalOpen: boolean;
  setIsCropModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cropImageSrc: string;
  setCropImageSrc: React.Dispatch<React.SetStateAction<string>>;
  cropImageName: string;
  setCropImageName: React.Dispatch<React.SetStateAction<string>>;
  cropedImage: {
    image: string;
    source: string;
  };
  setCropedImage: React.Dispatch<
    React.SetStateAction<{
      image: string;
      source: string;
    }>
  >;
  handleCropModal: (
    base64String: string,
    name: string,
    source?: string
  ) => void;
  filterListLoading: boolean;
  anyFilterFetching: boolean;
  resetPaginator: () => void;
  clearFilterList: (key: string) => void;
  filterApplyBtn: boolean;
  setFilterApplyBtn: React.Dispatch<React.SetStateAction<boolean>>;
  mobileFilterQuery: QueryData;
  setMobileFilterQuery: React.Dispatch<React.SetStateAction<QueryData>>;
  visitedSteps: string[];
  markStepVisited: (step: string) => void;
  partialFilledFieldsCategory: {
    about: boolean;
    details: boolean;
    provenance: boolean;
    location: boolean;
    image: boolean;
    objectIdentification: boolean;
    secondaryMeasurements: boolean;
    valuation: boolean;
    records: boolean;
    media: boolean;
    components: boolean;
  };
  appliedFilterDbIdWithLabel: { dbId: string; label: string }[];
  setAppliedFilterDbIdWithLabel: React.Dispatch<
    React.SetStateAction<{ dbId: string; label: string }[]>
  >;
}

const ObjectContext = createContext<ObjectContextType | undefined>(undefined);

const ObjectContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { addObjects, updateObject, getObjectById, objectList } = useObjects();
  const { isLogin } = useAuth();
  const { checkEligiblity } = useAsignProtect();
  const { me } = useUserContext();
  const {
    filterOption,
    setQuery,
    setFilterOption,
    removeAllQuery,
    setPaginatorData,
    refetchPaginationData,
    removeOptionKey,
    removeQueryKey,
    resetPaginator,
    filterListLoading,
    setFilterListLoadingList,
    mobileFilterQuery,
    setMobileFilterQuery,
  } = useFilter({
    options: {
      pagination: true,
      limit: 20,
      offset: 0,
      populate: "",
    },
  });
  const [valuationPricing, setValuationPricing] = useState();
  const [eligibility, setEligibility] = useState(false);
  const [isapPageVisited, setIsapPageVisited] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [falseBooleanFields, setFalseBooleanFields] = useState<string[]>([]);
  const [category, setCategory] = useState();
  const [filledFields, setFilledFields] = useState();
  const [objectIds, setObjectIds] = useState<string[]>([]);
  const [locations, setLocations] = useState({});
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const objectId = searchParams.get("oi");
  const isCatalogRaisonne = searchParams.get("catalogue_raisonne");
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string>("");
  const [cropImageName, setCropImageName] = useState("");
  const [filterApplyBtn, setFilterApplyBtn] = useState(false);
  const [cropedImage, setCropedImage] = useState<{
    image: string;
    source: string;
  }>({
    image: "",
    source: "",
  });
  const [partialFilledFieldsCategory, setPartialFilledFieldsCategory] =
    useState<{
      about: boolean;
      details: boolean;
      provenance: boolean;
      location: boolean;
      image: boolean;
      objectIdentification: boolean;
      secondaryMeasurements: boolean;
      valuation: boolean;
      records: boolean;
      media: boolean;
      components: boolean;
    }>({
      about: false,
      details: false,
      provenance: false,
      location: false,
      image: false,
      objectIdentification: false,
      secondaryMeasurements: false,
      valuation: false,
      records: false,
      media: false,
      components: false,
    });
  const [appliedFilterDbIdWithLabel, setAppliedFilterDbIdWithLabel] = useState<
    { dbId: string; label: string }[]
  >([]);

  const locationRouter = useLocation();

  // Handle initial catalogue_raisonne filter setup
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("catalogue_raisonne")) {
      setQuery({
        catalogueRaisonne: true,
        userId: searchParams.get("catalogue_raisonne_artist_id") || me?._id,
      });
    }
  }, [me?._id]);

  const computeKey = (id: string | null) => `visitedSteps_${id || "new"}`;

  const [visitedSteps, setVisitedSteps] = useState<string[]>(() => {
    const key = computeKey(objectId);
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!objectId) return;
    const newKey = computeKey(null);
    const currentKey = computeKey(objectId);
    const raw = sessionStorage.getItem(currentKey);
    if (!raw) {
      const rawNew = sessionStorage.getItem(newKey);
      if (rawNew) {
        sessionStorage.setItem(currentKey, rawNew);
        setVisitedSteps(JSON.parse(rawNew));
      }
    }
  }, [objectId]);

  const saveVisited = (data: string[]) => {
    sessionStorage.setItem(computeKey(objectId), JSON.stringify(data));
  };

  const markStepVisited = (step: string) => {
    setVisitedSteps((prev) => {
      if (prev.includes(step)) return prev;
      const updated = [...prev, step];
      saveVisited(updated);
      return updated;
    });
  };

  useEffect(() => {
    const path = locationRouter.pathname;
    const inFlow =
      path.startsWith("/catalog/add/object") ||
      path.startsWith("/catalog/edit/object");
    if (!inFlow) {
      sessionStorage.removeItem(computeKey(objectId));
      setVisitedSteps([]);
    }
  }, [locationRouter.pathname]);

  // Check object eligibility
  const eligibilityHandler = useMutation({
    mutationFn: checkEligiblity,
    onSuccess: ({ data }) => {
      const { missingFields, falseBooleanFields, isEligible } = data[0]; // Assuming data structure
      setMissingFields(missingFields);
      setFalseBooleanFields(falseBooleanFields);
      setEligibility(isEligible);
      setCategory(data[0].missingFieldCategory);
      setFilledFields(data[0].filledFieldsCategory);
      setPartialFilledFieldsCategory(data[0].partialFilledFieldsCategory);
    },
    onError: () => {},
  });

  // Fetch current object if objectId is present
  const currentObject = useQuery({
    queryKey: ["object", objectId],
    queryFn: () => getObjectById({ id: objectId as string }),
    enabled: !!objectId && isLogin,
  });

  // First, handle the URL parameters for catalogue_raisonne
  const qp = new URLSearchParams(window.location.search);
  const isCatalogueRaisonne = qp.has("catalogue_raisonne");
  const catalogueRaisonneArtistId = qp.get("catalogue_raisonne_artist_id");

  // Process the existing query parameters
  const cleanedQuery = Object.entries(filterOption?.query ?? {}).reduce(
    (acc: any, [key, value]) => {
      if (value) {
        if (["searchQuery", "catalogueRaisonne", "userId"].includes(key)) {
          acc[key] = value;
        } else {
          acc[key] = Array.isArray(value) ? value : [value];
        }
      }
      return acc;
    },
    {} as Record<string, unknown[] | string>
  );

  // Apply catalogue_raisonne filter if in the URL
  if (isCatalogueRaisonne) {
    cleanedQuery.catalogueRaisonne = true;
    if (catalogueRaisonneArtistId) {
      cleanedQuery.userId = catalogueRaisonneArtistId;
    } else if (me?._id) {
      cleanedQuery.userId = me._id;
    }
  }

  const filterOptionQuery: QueryData = {
    query: cleanedQuery,
    options: filterOption?.options,
  };

  // check if window size is mobile
  const isMobile = useMediaQuery("(max-width: 768px)");
  const objectsList = useQuery({
    queryKey: [
      "objectsList",
      isMobile
        ? {
            query: {
              searchQuery: filterOptionQuery?.query?.searchQuery,
              mobileFilter: true,
            },
            options: filterOption?.options,
          }
        : filterOptionQuery,
    ],
    queryFn: () => isLogin && objectList(filterOptionQuery),
    enabled: isLogin,
    //debug where its triggered and console it
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  type FilterKey =
    | "protect"
    | "registration"
    | "objectTypes"
    | "medium"
    | "surface"
    | "movements"
    | "materials"
    | "years"
    | "cities"
    | "artistName"
    | "locations"
    | "possession"
    | "size";

  const defaultKeys: FilterKey[] = [
    "protect",
    "registration",
    "objectTypes",
    "medium",
    "surface",
    "movements",
    "materials",
    "years",
    "cities",
    "artistName",
    "locations",
    "possession",
    "size",
  ];

  const [filterList, setFilterList] = useState<any>([]);

  const fetchFilterData = async ({
    key,
    apiBaseUrl,
    filterQuery,
  }: {
    key: FilterKey;
    apiBaseUrl: string;
    filterQuery: any;
  }) => {
    const params: any = {
      onlyMe: true,
      [`${key}Filter`]: true,
      type: filterQuery?.type,
      locations: filterQuery?.locations,
      possession: filterQuery?.possession,
      artistName: filterQuery?.artistName,
      creationYear: filterQuery?.creationYear,
      searchQuery: filterQuery?.searchQuery,
      protect: filterQuery?.protect,
      registration: filterQuery?.registration,
      size: filterQuery?.size,
      years: filterQuery?.years,
      medium: filterQuery?.medium,
      surface: filterQuery?.surface,
    };

    const response = await apis.get(apiBaseUrl, { params });
    const resultKey = key === "size" ? "maxSize" : key;
    return { key: resultKey, data: response.data?.data[resultKey] ?? [] };
  };

  const entries = useMemo(() => defaultKeys, []);
  const apiBaseUrl = useMemo(() => {
    const qp = new URLSearchParams(window.location.search);
    return qp.has("catalogue_raisonne_artist_id")
      ? "/client/api/v1/search/filterList/raisonne-artist"
      : "/client/api/v1/search/filterList/objects";
  }, []);
  const queries = useQueries({
    queries: entries.map((key) => ({
      queryKey: ["filter", key, filterOption],
      queryFn: () =>
        fetchFilterData({
          key,
          apiBaseUrl,
          filterQuery: filterOption?.query,
        }),
      enabled: !isMobile || filterApplyBtn,
      staleTime: 1000 * 60 * 60 * 2,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })),
  });
  const anyFilterFetching = queries.some((q) => q.isFetching);
  const formatLabel = (item: string) => {
    const words = item.trim().split(/\s+/);
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };
  queries.forEach( (q, idx) => {
    let key: any = entries[idx];
    useEffect( () => {
      async function fetchData() {
        if (q.data) {
          let data = q.data.data ?? [];
          if (key === "years") {
            key = "creationYear";
          }
          let queryData = filterOptionQuery["query"]
            ? (filterOptionQuery["query"][key] as string[])
            : undefined;
  
          if (key === "objectTypes") {
            queryData = filterOptionQuery["query"]
              ? (filterOptionQuery["query"]["type"] as string[])
              : undefined;
          }
  
          if (queryData && Array.isArray(queryData) && key !== "size") {
            const isArtistNameFilter = key === "artistName";
            const uniqueItems = new Map<string, any>();
          
            // Step 1: Process existing `data`
            await Promise.all(
              data.map(async (item: any) => {
                const itemKey = (item.artistId || item.label)?.toLowerCase();
                if (itemKey) {
                  uniqueItems.set(itemKey, {
                    ...item,
                    label: item.label || formatLabel(item.artistId || ""),
                    count:
                      typeof item.count === "number" || typeof item.count === "string"
                        ? item.count
                        : 0,
                    ...(isArtistNameFilter && {
                      artistId: item.artistId?.toLowerCase(),
                    }),
                    sortKey:
                      item.sortKey || (item.label || item.artistId || "").toUpperCase(),
                  });
                }
              })
            );
          
            // Step 2: Process `queryData`
            await Promise.all(
              queryData.map(async (item: string) => {
                if (!item) return;
          
                const itemKey = item.toLowerCase();        
                if (!uniqueItems.has(itemKey)) {
                  const isId = /^[0-9a-fA-F]{24}$/.test(item);
                  let newLabel = item;
      
                  // Example async lookup (simulated here with Promise.resolve)
                  if (isId) {
                    const artist = appliedFilterDbIdWithLabel.find(
                      (artistItem: any) =>
                        artistItem.dbId?.toLowerCase() === itemKey
                    );
                    newLabel = artist?.label || item;
                  }
                  if(Array.from(uniqueItems.values()).findIndex((item: any) => item.label.toLowerCase() === itemKey) === -1){
                    uniqueItems.set(itemKey, {
                      label: formatLabel(newLabel),
                      count: 0,
                      sortKey: newLabel.toUpperCase(),
                      ...(isArtistNameFilter && {
                        artistId: isId ? item.toLowerCase() : undefined,
                        ...(isId && { _isPending: true }),
                      }),
                    });
                  }
                }
              })
            );
          
            // Step 3: Final transformation
            const newData = Array.from(uniqueItems.values())
            .filter((item: any) => {
              return (
                (item.label || item.artistId) &&
                (typeof item.count !== "undefined" &&
                    item.count !== null &&
                    item.count !== "0")
              );
            })
            .sort((a: any, b: any) => a.sortKey.localeCompare(b.sortKey));
            data = newData;
          }
            
          setFilterList((prev: any) => ({
            ...prev,
            [q.data.key]: data,
          }));
        }
      }
      if (q.isError) {
        setFilterList((prev: any) => ({ ...prev, [key]: [] }));
      }
      fetchData();
    }, [q.data, q.isError]);
  });

  useEffect(() => {
    if (filterApplyBtn) {
      // Only refetch when filterApplyBtn is true
      objectsList.refetch();
      queries.forEach((q) => q.refetch());
      setFilterApplyBtn(false);
    }
  }, [filterApplyBtn]);

  // Handle mobile filter query changes
  useEffect(() => {
    if (isMobile) {
      setMobileFilterQuery({
        query: {
          ...filterOption?.query,
          searchQuery: filterOption?.query?.searchQuery,
        },
        options: filterOption?.options,
      });
    }
  }, [filterOption, isMobile]);

  useEffect(() => {
    if (isMobile) {
      setFilterApplyBtn(true);
    }
  }, []);

  // Sync mobileFilterQuery with filterOption when apply button is clicked
  useEffect(() => {
    if (filterApplyBtn && isMobile) {
      setMobileFilterQuery({
        query: {
          ...filterOption?.query,
        },
        options: filterOption?.options,
      });
    }
  }, [filterApplyBtn, isMobile, filterOption]);

  const clearFilterList = (key: string) => {
    !isMobile &&
      setFilterList((prev: any) => {
        const newFilterList = { ...prev };
        Object.keys(newFilterList).forEach((k) => {
          if (k !== key) {
            newFilterList[k] = [];
          }
        });
        return newFilterList;
      });
  };

  const addObjectHandler = useMutation({
    mutationFn: addObjects,
    onSuccess: (data) => {
      toast.dismiss();
      toast.success("Saved");
      navigate(`?oi=${data.id}`);
      queryClient.invalidateQueries({
        queryKey: ["object", data.id],
      }); // Refetch new object
      queryClient.invalidateQueries({
        queryKey: ["objectsList"],
      }); // Refetch updated object
      queryClient.invalidateQueries({
        queryKey: ["filterList"],
      }); // Refetch updated object
      eligibilityHandler.mutate({
        ids: [data.id],
        // params: { catalogue_raisonne: isCatalogRaisonne }, //no need [done by ansh]
      }); // Check eligibility after adding object
    },
    onError: () => {
      toast.dismiss();
      toast.error("Something went wrong");
    },
  });

  const updateObjectHandler = useMutation({
    mutationFn: updateObject,
    onMutate: () => {
      const isAddObjectPage = window.location.pathname.includes(
        "/catalog/add/object"
      );

      if (!isAddObjectPage) {
        toast.loading("Updating object...");
      }
    },
    onSuccess: () => {
      toast.dismiss();
      const message = window.location.pathname.includes("/catalog/add/object")
        ? "Saved"
        : "Object updated successfully";
      toast.success(message);

      queryClient.invalidateQueries({
        queryKey: ["object", objectId],
      }); // Refetch updated object
      queryClient.invalidateQueries({
        queryKey: ["objectsList"],
      }); // Refetch updated object
      queryClient.invalidateQueries({
        queryKey: ["filterList"],
      }); // Refetch updated object
      eligibilityHandler.mutate({
        ids: [objectId as string],
        // params: { catalogue_raisonne: isCatalogRaisonne }, //no need [done by ansh]
      });
    },

    onError: () => {
      toast.dismiss();
      toast.error("Something went wrong");
    },
  });

  const getObjectDiff = (oldObj: any, newObj: any) => {
    const diff: any = {};
    for (const key in newObj) {
      if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
        diff[key] = newObj[key];
      }
    }
    return diff;
  };

  const updateOrAddItem = async (data: any) => {
    if (objectId && currentObject.data) {
      const updatedFields = getObjectDiff(currentObject.data, data);
      if (Object.keys(updatedFields).length > 0) {
        await updateObjectHandler.mutateAsync({
          id: objectId,
          data: { ...updatedFields, objectId: currentObject?.data?.objectId },
        });
      }
    } else {
      const isAnyFieldFilled = Object.values(data).some(
        (field) => field !== "" && field !== null && field !== undefined
      );
      if (isAnyFieldFilled) {
        await addObjectHandler.mutateAsync(data);
      }
    }
  };

  const addOrDeleteObjectId = (id: string) => {
    const isIdAlreadyExist = objectIds.find((objId) => objId == id);

    if (isIdAlreadyExist) {
      setObjectIds(objectIds.filter((objId) => objId !== id));
    } else {
      setObjectIds([...objectIds, id]);
    }
  };

  const handleCropModal = (
    base64String: string,
    name: string,
    source?: string
  ) => {
    setCropImageSrc(base64String);
    setCropImageName(name);
    setIsCropModalOpen(true);
    if (source) {
      setCropedImage((prev) => ({
        ...prev,
        source,
      }));
    }
  };

  useEffect(() => {
    if (objectId) {
      eligibilityHandler.mutate({
        ids: [objectId],
        // params: { catalogue_raisonne: isCatalogRaisonne }, //no need [done by ansh]
      });
    } else {
      setEligibility(false);
      setMissingFields([]);
      setFalseBooleanFields([]);
      setCategory(undefined);
      setFilledFields(undefined);
    }
  }, [objectId]);

  useEffect(() => {
    if (!objectId) {
      const primaryLocation = me?.address.find(
        (location: any) => location.primary
      );
      if (!primaryLocation) return;
      setLocations({
        currentLocation: {
          ...primaryLocation,
          locationId: primaryLocation?._id,
          createdAt: new Date(),
        },
      });
    }
  }, [me?.address]);

  useEffect(() => {
    console.log("filterOption", filterOption);
  }, [filterOption]);

  const contextValue: ObjectContextType = {
    currentObject: currentObject.data,
    isLoading: currentObject.isLoading,
    updateOrAddItem,
    setFilterOption,
    setPaginatorData,
    refetchPaginationData,
    setQuery,
    removeQueryKey,
    filterOption,
    objectsList,
    eligibility,
    missingFields,
    falseBooleanFields,
    category,
    addOrDeleteObjectId,
    objectIds,
    setObjectIds,
    removeAllQuery,
    removeOptionKey,
    locations,
    setLocations,
    isapPageVisited,
    setIsapPageVisited,
    valuationPricing,
    setValuationPricing,
    filterList,
    isCropModalOpen,
    setIsCropModalOpen,
    cropImageSrc,
    setCropImageSrc,
    cropImageName,
    setCropImageName,
    cropedImage,
    setCropedImage,
    handleCropModal,
    filledFields,
    setFilledFields,
    filterListLoading,
    resetPaginator,
    anyFilterFetching,
    clearFilterList,
    filterApplyBtn,
    setFilterApplyBtn,
    mobileFilterQuery,
    setMobileFilterQuery,
    visitedSteps,
    markStepVisited,
    partialFilledFieldsCategory,
    appliedFilterDbIdWithLabel,
    setAppliedFilterDbIdWithLabel,
  };

  return (
    <ObjectContext.Provider value={contextValue}>
      {children}
    </ObjectContext.Provider>
  );
};

export const useObjectContext = () => {
  const context = useContext(ObjectContext);
  if (!context) {
    throw new Error(
      "useObjectContext must be used within ObjectContextProvider"
    );
  }
  return context;
};

export default ObjectContextProvider;
